<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="customers"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :scroll="{ x: 1280 }"
        class="text-nowrap align-middle people-table"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("customer_page.no_customers_found") }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'code'">
            <span class="fw-semibold text-primary">{{ record.code || t("customer_page.not_available_short") }}</span>
          </template>

          <template v-else-if="column && column.key === 'deposit'">
            {{ formatCurrency(record.deposit) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="Number(record.status || 0) === 1 ? 'status-active' : 'status-inactive'">
              {{ Number(record.status || 0) === 1 ? t("customer_page.status_active") : t("customer_page.status_inactive") }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('customer_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#customerDetailsModal"
                @click="$emit('view-customer', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('customer_page.view')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('customer_page.edit')"
                data-bs-toggle="modal"
                data-bs-target="#customerCreateModal"
                @click="$emit('edit-customer', record)"
              >
                <img src="../../../assets/img/icons/edit.svg" :alt="t('customer_page.edit')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('customer_page.delete')"
                @click="$emit('delete-customer', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('customer_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { Customer } from "../../../services/customer";

export default defineComponent({
  name: "CustomerList",
  props: {
    customers: {
      type: Array as () => Customer[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["view-customer", "edit-customer", "delete-customer"],
  setup() {
    const { t } = useI18n();

    const columns = computed(() => [
      { title: t("customer_page.customer_code"), dataIndex: "code", key: "code", align: "center" as const, width: 150 },
      { title: t("customer_page.customer_name"), dataIndex: "name", key: "name", align: "center" as const, width: 220 },
      { title: t("customer_page.email"), dataIndex: "email", key: "email", align: "center" as const, width: 220 },
      { title: t("customer_page.phone"), dataIndex: "phone", key: "phone", align: "center" as const, width: 160 },
      { title: t("customer_page.country"), dataIndex: "country", key: "country", align: "center" as const, width: 160 },
      { title: t("customer_page.address"), dataIndex: "address", key: "address", align: "center" as const, width: 220 },
      { title: t("customer_page.deposit"), key: "deposit", align: "center" as const, width: 140 },
      { title: t("customer_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("customer_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);

    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("customer_page.items_per_page") },
    }));

    const formatCurrency = (value?: number) => `$${Number(value || 0).toFixed(2)}`;

    return {
      t,
      columns,
      paginationConfig,
      formatCurrency,
    };
  },
});
</script>

<style scoped>
.people-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.people-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  color: #1f9254;
  background: #e8f7ee;
}

.status-inactive {
  color: #d33c43;
  background: #fdecec;
}
</style>
